import { useCallback, useEffect, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { $getRoot, $createParagraphNode, FORMAT_TEXT_COMMAND, FORMAT_ELEMENT_COMMAND, UNDO_COMMAND, REDO_COMMAND, LexicalEditor } from 'lexical';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $generateNodesFromDOM } from '@lexical/html';

interface BlogEditorProps {
  initialHtml?: string;
  onChange?: (html: string) => void;
}

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();

  const formatBold = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  const formatItalic = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  const formatUnderline = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
  const formatHeading1 = () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'h1');
  const formatHeading2 = () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'h2');
  const formatHeading3 = () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'h3');
  const formatBulletList = () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
  const formatOrderedList = () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
  const formatLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, { url, target: '_blank' });
    }
  };
  const undo = () => editor.dispatchCommand(UNDO_COMMAND, undefined);
  const redo = () => editor.dispatchCommand(REDO_COMMAND, undefined);

  const btnBase = 'w-8 h-8 flex items-center justify-center rounded text-silver-grey hover:text-warm-white hover:bg-royal-navy/40 transition-colors text-xs cursor-pointer';
  const btnActive = 'bg-royal-navy/50 text-champagne-gold';

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 bg-royal-navy/20 border border-royal-navy/40 rounded-t-lg flex-wrap">
      <button onClick={undo} className={btnBase} title="Undo">
        <i className="ri-arrow-go-back-line" />
      </button>
      <button onClick={redo} className={btnBase} title="Redo">
        <i className="ri-arrow-go-forward-line" />
      </button>
      <span className="w-px h-5 bg-royal-navy/50 mx-1" />
      <button onClick={formatBold} className={btnBase} title="Bold">
        <i className="ri-bold" />
      </button>
      <button onClick={formatItalic} className={btnBase} title="Italic">
        <i className="ri-italic" />
      </button>
      <button onClick={formatUnderline} className={btnBase} title="Underline">
        <i className="ri-underline" />
      </button>
      <span className="w-px h-5 bg-royal-navy/50 mx-1" />
      <button onClick={formatHeading1} className={btnBase} title="Heading 1">
        <span className="font-bold text-xs">H1</span>
      </button>
      <button onClick={formatHeading2} className={btnBase} title="Heading 2">
        <span className="font-bold text-xs">H2</span>
      </button>
      <button onClick={formatHeading3} className={btnBase} title="Heading 3">
        <span className="font-bold text-xs">H3</span>
      </button>
      <span className="w-px h-5 bg-royal-navy/50 mx-1" />
      <button onClick={formatBulletList} className={btnBase} title="Bullet List">
        <i className="ri-list-unordered" />
      </button>
      <button onClick={formatOrderedList} className={btnBase} title="Numbered List">
        <i className="ri-list-ordered" />
      </button>
      <span className="w-px h-5 bg-royal-navy/50 mx-1" />
      <button onClick={formatLink} className={btnBase} title="Insert Link">
        <i className="ri-link" />
      </button>
    </div>
  );
}

function HtmlExportPlugin({ onChange }: { onChange?: (html: string) => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!onChange) return;
    const unregister = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const html = exportHtml(editor);
        onChange(html);
      });
    });
    return unregister;
  }, [editor, onChange]);

  return null;
}

function exportHtml(editor: LexicalEditor): string {
  let html = '';
  editor.getEditorState().read(() => {
    const root = $getRoot();
    const children = root.getChildren();
    html = children
      .map((node) => {
        const type = node.getType();
        const text = node.getTextContent();
        if (type === 'heading') {
          const tag = (node as any).getTag?.() || 'h2';
          return `<${tag}>${text}</${tag}>`;
        }
        if (type === 'quote') {
          return `<blockquote>${text}</blockquote>`;
        }
        if (type === 'list') {
          const listType = (node as any).getListType?.() || 'bullet';
          const tag = listType === 'number' ? 'ol' : 'ul';
          const items = node.getChildren()
            .map((li) => `<li>${li.getTextContent()}</li>`)
            .join('');
          return `<${tag}>${items}</${tag}>`;
        }
        if (type === 'paragraph') {
          return `<p>${text}</p>`;
        }
        return `<p>${text}</p>`;
      })
      .join('\n');
  });
  return html;
}

function InitialContentPlugin({ initialHtml }: { initialHtml?: string }) {
  const [editor] = useLexicalComposerContext();
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialHtml || initialized.current) return;
    initialized.current = true;

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      const parser = new DOMParser();
      const dom = parser.parseFromString(initialHtml, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);

      if (nodes.length > 0) {
        root.select();
        const selection = root.selectEnd();
        if (selection) {
          selection.insertNodes(nodes);
        }
      } else {
        root.append($createParagraphNode());
      }
    });
  }, [editor, initialHtml]);

  return null;
}

const editorTheme = {
  ltr: 'text-left',
  rtl: 'text-right',
  placeholder: 'text-taupe-grey text-sm',
  paragraph: 'text-silver-grey text-sm leading-relaxed mb-2',
  heading: {
    h1: 'text-2xl font-heading font-bold text-warm-white mb-3 mt-4',
    h2: 'text-xl font-heading font-semibold text-warm-white mb-3 mt-3',
    h3: 'text-lg font-heading font-semibold text-warm-white mb-2 mt-2',
  },
  list: {
    ul: 'list-disc pl-5 text-silver-grey text-sm mb-2 space-y-1',
    ol: 'list-decimal pl-5 text-silver-grey text-sm mb-2 space-y-1',
    listitem: 'text-silver-grey text-sm',
  },
  quote: 'border-l-2 border-champagne-gold/50 pl-4 italic text-silver-grey text-sm my-3',
  link: 'text-champagne-gold underline',
  text: {
    bold: 'font-bold text-warm-white',
    italic: 'italic text-champagne-gold',
    underline: 'underline',
  },
};

function onError(error: Error) {
  console.error('Lexical Editor Error:', error);
}

export default function BlogEditor({ initialHtml, onChange }: BlogEditorProps) {
  const initialConfig = {
    namespace: 'BlogEditor',
    theme: editorTheme,
    onError,
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="border border-royal-navy/40 rounded-lg overflow-hidden bg-midnight">
        <ToolbarPlugin />
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="min-h-[300px] p-4 outline-none text-silver-grey text-sm leading-relaxed" />
            }
            placeholder={
              <div className="absolute top-4 left-4 text-taupe-grey text-sm pointer-events-none select-none">
                Start writing your blog post...
              </div>
            }
          />
        </div>
      </div>
      <HistoryPlugin />
      <HtmlExportPlugin onChange={onChange} />
      <InitialContentPlugin initialHtml={initialHtml} />
    </LexicalComposer>
  );
}